import {
  ORDER_RESTOCKING_FEE_MAX_RATE,
  ORDER_RESTOCKING_FEE_MIN_RATE,
  TAX_RATE,
} from '@/config/constants'
import { buildBreakdown, round2 } from '@/lib/services/pricing/money-math'

export type PriceTier = 'RETAIL' | 'WHOLESALE'

/**
 * The one sales-tax rate applied to product orders. Shared by the cart page
 * (selectCartStats), the checkout review step, and the server-side reserve
 * (reserveOrderAction), so a displayed total and the charged total can never
 * disagree on tax. Points at the canonical TAX_RATE (5% GST) so the products
 * pipeline and the /pay appointment breakdown never diverge on tax.
 */
export const ORDER_TAX_RATE = TAX_RATE

export interface CartSummary {
  subtotal: number
  taxRate: number
  tax: number
  /** subtotal + tax (shipping is added later, at checkout) */
  grandTotal: number
}

/**
 * Single source of truth for the cart money breakdown (subtotal, tax, grand
 * total) so the drawer and the /cart page can never drift. `subtotal` is the
 * tier-resolved sum of line totals (selectCartTotal). Shipping is not known
 * until checkout, so grandTotal here is pre-shipping.
 *
 * The restocking fee (ORDER_RESTOCKING_FEE_MIN/MAX_RATE in config/constants)
 * is deliberately NOT part of this breakdown: customers pay the full total at
 * checkout, and the fee only exists as a refund withholding on cancellation
 * (B1 decision, 2026-07-19). The cart shows it as a policy disclosure, never
 * as a due-now/balance split.
 *
 * PHYSICALLY CONVERGED (ledger B6, completed after service-quotes extracted
 * the pure core): derives from the SAME buildBreakdown the appointment /pay
 * page and financial snapshots use, via the import-free
 * src/lib/services/pricing/money-math.ts (safe in this client-bundled file).
 * The cart intentionally exposes only subtotal/tax/grandTotal from it: the
 * reservationFee/balanceDue fields are the appointment down-payment model,
 * not the order model (orders charge in full, B1).
 * tests/unit/orders/cart-summary-contract.test.ts guards against anyone
 * re-inlining local math here.
 */
export function computeCartSummary(subtotal: number): CartSummary {
  const { subtotal: s, tax, grandTotal } = buildBreakdown(subtotal)
  return {
    subtotal: s,
    taxRate: ORDER_TAX_RATE,
    tax,
    grandTotal,
  }
}

export interface RestockingRefund {
  feeRate: number
  restockingFee: number
  refundAmount: number
}

/**
 * Tiered restocking-fee withholding for cancelled paid orders (B1 decision,
 * 2026-07-19): PROCESSING cancels withhold the MIN rate (20%), COMPLETED
 * (fulfilled) refunds withhold the MAX rate (30%). Cent-exact by construction:
 * restockingFee + refundAmount always reconciles to chargedAmount.
 */
export function computeRestockingRefund(
  chargedAmount: number,
  tier: 'PROCESSING' | 'COMPLETED'
): RestockingRefund {
  const feeRate = tier === 'COMPLETED' ? ORDER_RESTOCKING_FEE_MAX_RATE : ORDER_RESTOCKING_FEE_MIN_RATE
  const restockingFee = round2(chargedAmount * feeRate)
  return { feeRate, restockingFee, refundAmount: round2(chargedAmount - restockingFee) }
}

export interface VariantPricing {
  retailPrice: number | null
  wholesalePrice: number | null
  rollPrice: number | null
  rollLengthUnits: number | null
}

export function resolvePrice(
  variant: VariantPricing,
  tier: PriceTier
): number | null {
  if (tier === 'WHOLESALE') {
    return variant.wholesalePrice ?? variant.retailPrice
  }
  return variant.retailPrice
}

export function resolveRollSavingsPercent(
  variant: VariantPricing,
  tier: PriceTier
): number | null {
  const perUnit = resolvePrice(variant, tier)
  if (!perUnit || !variant.rollPrice || !variant.rollLengthUnits) return null
  const cutTotal = perUnit * Number(variant.rollLengthUnits)
  const rollTotal = Number(variant.rollPrice)
  if (cutTotal <= 0) return null
  return Math.round(((cutTotal - rollTotal) / cutTotal) * 100)
}

export type LinePurchaseMode = 'custom-cut' | 'full-roll' | 'fixed'

type LineTotalParams =
  | { purchaseMode: 'custom-cut'; lengthPerPiece: number; pieces: number; pricePerUnit: number }
  | { purchaseMode: 'full-roll'; quantity: number; rollPrice: number }
  | { purchaseMode: 'fixed'; quantity: number; unitPrice: number }

export function calculateLineTotal(params: LineTotalParams): number {
  switch (params.purchaseMode) {
    case 'custom-cut':
      return params.lengthPerPiece * params.pieces * params.pricePerUnit
    case 'full-roll':
      return params.quantity * params.rollPrice
    case 'fixed':
      return params.quantity * params.unitPrice
  }
}

export interface LineUnitPriceInput {
  purchaseMode: LinePurchaseMode
  tier: PriceTier
  /** Fresh DB pricing for the variant the customer selected, if any. */
  variant?: (VariantPricing & { attributeValues?: unknown; priceBreaks?: unknown }) | null
  /** Fallback product-level pricing (used for simple, variant-less lines). */
  product: { price?: number | null; salePrice?: number | null }
  /** Total yards of this custom-cut line (lengthPerPiece x pieces). Enables
   *  volume-tier pricing; omitted (or non-cut) lines use the base rate. */
  lineYards?: number | null
}

/** One volume-pricing break: this per-yard rate applies from minYards up. */
export interface YardPriceBreak {
  minYards: number
  perYard: number
}

/** Parse a priceBreaks JSON column into usable breaks: positive numbers only,
 *  sorted ascending by minYards. Returns null when empty or malformed, so
 *  callers fall back to the flat base rate instead of pricing off garbage. */
export function normalizePriceBreaks(raw: unknown): YardPriceBreak[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null
  const breaks: YardPriceBreak[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') return null
    const minYards = Number((entry as Record<string, unknown>).minYards)
    const perYard = Number((entry as Record<string, unknown>).perYard)
    if (!Number.isFinite(minYards) || !Number.isFinite(perYard) || minYards < 1 || perYard <= 0) return null
    breaks.push({ minYards, perYard })
  }
  return breaks.sort((a, b) => a.minYards - b.minYards)
}

/** The per-yard rate for a given total-yard quantity: the largest break whose
 *  minYards does not exceed the quantity, else the fallback flat rate. */
export function resolveTieredPerYard(
  breaks: YardPriceBreak[] | null | undefined,
  yards: number,
  fallback: number
): number {
  if (!breaks || breaks.length === 0) return fallback
  let rate: number | null = null
  for (const b of breaks) {
    if (b.minYards <= Math.max(1, yards)) rate = b.perYard
    else break
  }
  return rate ?? fallback
}

/**
 * The jump-to-roll threshold: the smallest whole-yard cut whose price meets or
 * exceeds the full-roll price. From that length up, a cut is never sold; the
 * customer gets the whole roll for the roll price instead (so cuts are sold
 * only at 1..crossover-1 yards). Null when the variant has no roll pricing.
 */
export function rollCrossoverYards(input: {
  breaks?: YardPriceBreak[] | null
  baseRate: number
  rollPrice?: number | null
  rollLengthUnits?: number | null
}): number | null {
  const rollPrice = input.rollPrice ?? null
  const rollLength = Math.round(Number(input.rollLengthUnits ?? 0))
  if (!rollPrice || rollPrice <= 0 || rollLength <= 0) return null
  for (let n = 1; n <= rollLength; n++) {
    if (n * resolveTieredPerYard(input.breaks, n, input.baseRate) >= rollPrice) return n
  }
  return rollLength
}

/** Coerces a Prisma Decimal (or any numeric-ish value) to a plain number,
 *  preserving null/undefined instead of collapsing them to 0 -- callers that
 *  need a fallback chain (e.g. resolveLineUnitPrice) depend on the distinction. */
export function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

/** The minimal per-line shape `calculateShippingCost` consumes: it reads only
 *  `quantity` and `product.price` off each line. */
export interface ShippingCalculatorLine {
  quantity: number
  product: { price: number }
}

/**
 * Canonical shipping-calculator line, built the SAME way by the checkout Review
 * estimate (client) and the reserve wrapper (server) so the two never drift.
 *
 * The mock calculator branches on per-line `product.price` (its weight
 * heuristic, `> 100`) and on the summed `product.price * quantity` (its
 * free-shipping threshold, `> 150`). Feeding it the resolved UNIT price plus the
 * customer-facing quantity makes both branches land on the numbers the customer
 * actually saw: a custom-cut line's quantity is its true cut length
 * (`lengthPerPiece * pieces`), NOT the reserve wrapper's rounded-up
 * inventory-hold quantity; a full-roll or fixed line's is its whole quantity.
 */
export function toShippingCalculatorLine(params: {
  purchaseMode: LinePurchaseMode
  unitPrice: number
  quantity: number
  lengthPerPiece?: number | null
  pieces?: number | null
}): ShippingCalculatorLine {
  const quantity =
    params.purchaseMode === 'custom-cut'
      ? (params.lengthPerPiece ?? 0) * (params.pieces ?? 1)
      : params.quantity
  return { quantity, product: { price: params.unitPrice } }
}

function attributeNumber(attributeValues: unknown, key: string): number | null {
  if (!attributeValues || typeof attributeValues !== 'object') return null
  const raw = (attributeValues as Record<string, unknown>)[key]
  const value = typeof raw === 'string' ? Number(raw) : typeof raw === 'number' ? raw : NaN
  return Number.isFinite(value) && value > 0 ? value : null
}

/**
 * Server-authoritative per-unit (or per-roll) price for a single purchase line.
 *
 * Mirrors the cart's client-side fallback chain (variant column -> variant
 * attributeValues -> product price) but resolves the customer's price tier from
 * the DB first, so the server never trusts client dollars. Feed the result into
 * `calculateLineTotal` with the same purchase mode the cart used and the two
 * sides agree on the line total.
 */
export function resolveLineUnitPrice(input: LineUnitPriceInput): number {
  const { purchaseMode, tier, variant, product } = input
  const productPrice = product.salePrice ?? product.price ?? 0

  if (purchaseMode === 'full-roll') {
    const rollPrice = variant?.rollPrice ?? attributeNumber(variant?.attributeValues, 'ROLL_PRICE')
    if (rollPrice && rollPrice > 0) return rollPrice
    return productPrice
  }

  const tierPrice = variant ? resolvePrice(variant, tier) : null
  const perUnit =
    (tierPrice && tierPrice > 0 ? tierPrice : null) ?? attributeNumber(variant?.attributeValues, 'PRICE_PER')
  if (perUnit && perUnit > 0) {
    // Volume-tier pricing (retail only: the breaks column carries derived
    // RETAIL rates; wholesale keeps its manual column untouched).
    if (
      purchaseMode === 'custom-cut' &&
      tier === 'RETAIL' &&
      input.lineYards != null &&
      input.lineYards > 0
    ) {
      return resolveTieredPerYard(normalizePriceBreaks(variant?.priceBreaks), input.lineYards, perUnit)
    }
    return perUnit
  }
  return productPrice
}

export interface LineIdentityInput {
  productId: string
  variantId?: string | null
  purchaseMode: LinePurchaseMode
  lengthPerPiece?: number | null
}

/**
 * Selection identity for one purchase line: the four-part tuple
 * (product, variant, purchase mode, cut length) serialized to a stable
 * string. ONE function computes it everywhere it exists: the client cart
 * merge key, the cart React list key, and the persisted OrderItem.lineKey
 * written at reserve time, so cart identity and order identity can never
 * drift (spec R5). A roll's length is fixed by the variant and a fixed line
 * has no length, so neither is a free axis: they collapse to sentinels.
 */
export function cartLineIdentityKey(input: LineIdentityInput): string {
  const length =
    input.purchaseMode === 'custom-cut'
      ? String(input.lengthPerPiece ?? 0)
      : input.purchaseMode === 'full-roll'
        ? 'ROLL'
        : 'na'
  return `${input.productId}::${input.variantId ?? 'none'}::${input.purchaseMode}::${length}`
}

/** The single source of the supplier markup: retail = round2(cost x rate).
 *  markupRate is a stored column (default 1.30); 30% is data, not a literal. */
export function deriveRetailFromCost(cost: number, markupRate: number): number {
  return round2(cost * markupRate)
}
